import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { MAX_ACTIVE_KNOWLEDGE_PER_TYPE } from "~/constants/knowledge";
import { TOAST_MESSAGES } from "~/constants/messages";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import type { Knowledge } from "~/interface/knowledge";
import styles from "./PendingKnowledgeModal.module.scss";

interface PendingKnowledgeModalProps {
  pendingKnowledges: Knowledge[];
  allKnowledges: Knowledge[];
  onClose: () => void;
  onApprove: (knowledge: Knowledge) => Promise<void>;
  onDiscard: (knowledge: Knowledge) => Promise<void>;
  onToggleActive?: ((knowledge: Knowledge) => Promise<void>) | undefined;
  onRemoveFromPending: (knowledgeId: string) => void;
}

const PendingKnowledgeModal: React.FC<PendingKnowledgeModalProps> = ({
  pendingKnowledges,
  allKnowledges,
  onClose,
  onApprove,
  onDiscard,
  onToggleActive,
  onRemoveFromPending,
}) => {
  const { canManageKnowledge } = useProjectPermission();
  const [searchFilter, setSearchFilter] = useState<Knowledge["type"] | "all">("all");
  const [selectedKnowledges, setSelectedKnowledges] = useState<Set<string>>(new Set());
  const [modifiedTypes, setModifiedTypes] = useState<Record<string, Knowledge["type"]>>({});
  const [approvedKnowledgeIds, setApprovedKnowledgeIds] = useState<Set<string>>(new Set());
  const [activatingKnowledgeId, setActivatingKnowledgeId] = useState<string | null>(null);

  // ドキュメントタイプごとの統計を計算
  const getKnowledgeStats = useCallback(
    (type: Knowledge["type"]) => {
      const allOfType = allKnowledges.filter((k) => k.type === type);
      const activeOfType = allOfType.filter((k) => k.isActive);
      return {
        activeCount: activeOfType.length,
        totalCount: allOfType.length,
      };
    },
    [allKnowledges],
  );

  // フィルタリングされたナレッジリスト（有効化済みを優先してソート）
  const filteredKnowledges = useMemo(() => {
    const filtered =
      searchFilter === "all"
        ? pendingKnowledges
        : pendingKnowledges.filter((k) => k.type === searchFilter);

    // 有効化済みのナレッジを上に表示
    return filtered.sort((a, b) => {
      const aStats = getKnowledgeStats(a.type);
      const bStats = getKnowledgeStats(b.type);

      // 有効化済み数が多い順
      if (aStats.activeCount !== bStats.activeCount) {
        return bStats.activeCount - aStats.activeCount;
      }

      // 同じなら作成日時順
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [pendingKnowledges, searchFilter, getKnowledgeStats]);

  const knowledgeList = filteredKnowledges;
  const totalCount = knowledgeList.length;

  // ドキュメントタイプ選択肢
  const TYPE_OPTIONS: Array<{ value: Knowledge["type"]; label: string }> = [
    { value: "system-proposal", label: "システム概要書" },
    { value: "functional-requirements", label: "機能要件書" },
    { value: "non-functional-requirements", label: "非機能要件書" },
    { value: "app-design", label: "アプリ設計書" },
    { value: "field-design", label: "フィールド設計書" },
    { value: "customize-specification", label: "カスタマイズ仕様書" },
  ];

  // 生成日を日本語形式で取得
  const getFormattedDate = (dateString: string): string => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${month}/${day}`;
    } catch (error) {
      console.error("Date formatting error:", error);
      return "";
    }
  };

  // 【承認処理】: 「承認」ボタンを押した時
  const handleApprove = async (knowledge: Knowledge) => {
    try {
      // 変更されたタイプがあれば適用
      const modifiedType = modifiedTypes[knowledge.id];
      const finalKnowledge: Knowledge = modifiedType
        ? { ...knowledge, type: modifiedType }
        : knowledge;

      console.log("💾 Approving meta prompt:", {
        id: finalKnowledge.id,
        title: finalKnowledge.title,
        type: finalKnowledge.type,
        remainingCount: totalCount - 1,
      });

      // 承認処理
      await onApprove(finalKnowledge);

      console.log("✅ Meta prompt approved successfully");

      // 承認済みリストに追加（有効化確認を表示）
      setApprovedKnowledgeIds((prev) => {
        const newSet = new Set(prev);
        newSet.add(knowledge.id);
        return newSet;
      });
    } catch (error) {
      console.error("🚨 Failed to approve meta prompt:", error);
      toast.error(TOAST_MESSAGES.KNOWLEDGE.APPROVAL_ERROR);
    }
  };

  // 【有効化処理】: 「有効化」ボタンを押した時
  const handleActivateKnowledge = async (knowledge: Knowledge) => {
    setActivatingKnowledgeId(knowledge.id);
    try {
      const finalKnowledge: Knowledge = modifiedTypes[knowledge.id]
        ? { ...knowledge, type: modifiedTypes[knowledge.id]! }
        : knowledge;

      // 有効化数制限のチェック
      const activeCount = allKnowledges.filter(
        (k) => k.type === finalKnowledge.type && k.isActive,
      ).length;

      if (activeCount >= MAX_ACTIVE_KNOWLEDGE_PER_TYPE) {
        // 上限に達している場合、エラーを表示して候補から削除（無効状態で保存される）
        toast.error(
          `このドキュメントタイプの有効化数は最大${MAX_ACTIVE_KNOWLEDGE_PER_TYPE}件までです。ナレッジは保存されましたが、有効化されていません。`,
        );

        // pendingKnowledgesから削除（カードを非表示に）
        onRemoveFromPending(knowledge.id);

        // 承認済みリストからも削除
        setApprovedKnowledgeIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(knowledge.id);
          return newSet;
        });

        setActivatingKnowledgeId(null);
        return;
      }

      // 有効化処理（トースト通知はonToggleActive内で表示される）
      if (onToggleActive) {
        await onToggleActive(finalKnowledge);
      }

      // pendingKnowledgesから削除（カードを非表示に）
      onRemoveFromPending(knowledge.id);

      // 承認済みリストからも削除
      setApprovedKnowledgeIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(knowledge.id);
        return newSet;
      });
    } catch (error) {
      console.error("🚨 Failed to activate knowledge:", error);
      toast.error(TOAST_MESSAGES.KNOWLEDGE.ACTIVATE_ERROR);
    } finally {
      setActivatingKnowledgeId(null);
    }
  };

  // 【後で処理】: 「後で」ボタンを押した時
  const handleSkipActivation = (knowledge: Knowledge) => {
    // pendingKnowledgesから削除（カードを非表示に）
    onRemoveFromPending(knowledge.id);

    // 承認済みリストからも削除
    setApprovedKnowledgeIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(knowledge.id);
      return newSet;
    });
  };

  const handleDiscard = async (knowledge: Knowledge) => {
    try {
      console.log("🗑️ Discarding meta prompt:", {
        id: knowledge.id,
        title: knowledge.title,
        remainingCount: totalCount - 1,
      });

      await onDiscard(knowledge);

      // 成功時のtoast通知
      toast.success(TOAST_MESSAGES.KNOWLEDGE.CANDIDATE_DISCARDED);
      console.log("✅ Meta prompt discarded successfully");

      // 残りがなくなったらモーダルを閉じる
      if (totalCount <= 1) {
        toast.success(TOAST_MESSAGES.KNOWLEDGE.ALL_CANDIDATES_PROCESSED);
        onClose();
      }
    } catch (error) {
      console.error("🚨 Failed to discard meta prompt:", error);
      toast.error(TOAST_MESSAGES.KNOWLEDGE.DISCARD_ERROR);
    }
  };

  // 全選択ハンドラー
  const handleSelectAll = () => {
    const allIds = new Set(knowledgeList.map((k) => k.id));
    setSelectedKnowledges(allIds);
  };

  // すべてクリアハンドラー
  const handleClearAll = () => {
    setSelectedKnowledges(new Set());
  };

  // 選択トグルハンドラー
  const handleToggleSelect = (id: string) => {
    setSelectedKnowledges((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // ドキュメントタイプ変更ハンドラー
  const handleTypeChange = (id: string, newType: Knowledge["type"]) => {
    setModifiedTypes((prev) => ({
      ...prev,
      [id]: newType,
    }));
  };

  // 選択された項目の一括承認
  const handleApproveSelected = async () => {
    const selected = knowledgeList.filter((k) => selectedKnowledges.has(k.id));
    for (const knowledge of selected) {
      await handleApprove(knowledge);
    }
    setSelectedKnowledges(new Set());
  };

  // 選択された項目の一括破棄
  const handleDiscardSelected = async () => {
    const selected = knowledgeList.filter((k) => selectedKnowledges.has(k.id));
    for (const knowledge of selected) {
      await handleDiscard(knowledge);
    }
    setSelectedKnowledges(new Set());
  };

  return (
    <div className={styles["modalContent"]}>
      {/* 検索バーとアクションバー */}
      <div className={styles["topBar"]}>
        <div className={styles["filterSection"]}>
          <select
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value as Knowledge["type"] | "all")}
            className={styles["searchSelect"]}
          >
            <option value="all">すべてのドキュメント</option>
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className={styles["filterCount"]}>{totalCount}件</span>
        </div>

        <div className={styles["actionButtons"]}>
          <button onClick={handleSelectAll} className={styles["actionButton"]}>
            全選択
          </button>
          <button onClick={handleClearAll} className={styles["actionButton"]}>
            すべてクリア
          </button>
          {selectedKnowledges.size > 0 && canManageKnowledge() && (
            <>
              <button onClick={handleApproveSelected} className={styles["bulkApproveButton"]}>
                選択を承認 ({selectedKnowledges.size})
              </button>
              <button onClick={handleDiscardSelected} className={styles["bulkDiscardButton"]}>
                選択を破棄 ({selectedKnowledges.size})
              </button>
            </>
          )}
        </div>
      </div>

      {/* カードリスト */}
      <div className={styles["cardList"]}>
        {knowledgeList.map((knowledge) => {
          const currentType = modifiedTypes[knowledge.id] || knowledge.type;
          const date = getFormattedDate(knowledge.created_at);
          const stats = getKnowledgeStats(currentType);

          const isSelected = selectedKnowledges.has(knowledge.id);

          return (
            <div
              key={knowledge.id}
              className={`${styles["knowledgeCard"]} ${isSelected ? styles["selected"] : ""}`}
            >
              <div className={styles["cardHeader"]}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggleSelect(knowledge.id)}
                  className={styles["cardCheckbox"]}
                />
                <div className={styles["cardInfo"]}>
                  <span className={styles["cardLabel"]}>ドキュメント:</span>
                  <div className={styles["cardTypeContainer"]}>
                    <select
                      value={currentType}
                      onChange={(e) =>
                        handleTypeChange(knowledge.id, e.target.value as Knowledge["type"])
                      }
                      className={styles["cardTypeSelect"]}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <span className={styles["knowledgeCount"]}>
                      有効: {stats.activeCount}/{MAX_ACTIVE_KNOWLEDGE_PER_TYPE}件
                    </span>
                  </div>
                </div>
                <div className={styles["cardInfo"]}>
                  <span className={styles["cardLabel"]}>生成情報:</span>
                  <span className={styles["cardValue"]}>
                    {date ? `${date} アップロードから自動生成` : "アップロードから自動生成"}
                  </span>
                </div>
              </div>

              <div className={styles["cardBody"]}>
                <div className={styles["cardSection"]}>
                  <h4 className={styles["cardSectionTitle"]}>タイトル</h4>
                  <p className={styles["cardText"]}>{knowledge.title || "無題"}</p>
                </div>

                <div className={styles["cardSection"]}>
                  <h4 className={styles["cardSectionTitle"]}>プロンプト</h4>
                  <p className={styles["cardTextArea"]}>{knowledge.meta_prompt || ""}</p>
                </div>
              </div>

              {approvedKnowledgeIds.has(knowledge.id) ? (
                // 承認済み：有効化確認を表示
                <div className={styles["cardFooterApproved"]}>
                  <div className={styles["approvedPrompt"]}>このナレッジを有効化しますか？</div>
                  {canManageKnowledge() && (
                    <div className={styles["cardFooterButtons"]}>
                      <button
                        onClick={() => handleActivateKnowledge(knowledge)}
                        className={styles["activateButton"]}
                        disabled={activatingKnowledgeId === knowledge.id}
                      >
                        {activatingKnowledgeId === knowledge.id ? "有効化中..." : "有効化"}
                      </button>
                      <button
                        onClick={() => handleSkipActivation(knowledge)}
                        className={styles["laterButton"]}
                        disabled={activatingKnowledgeId === knowledge.id}
                      >
                        後で
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                // 未承認：承認・破棄ボタンを表示
                canManageKnowledge() && (
                  <div className={styles["cardFooterButtons"]}>
                    <button
                      onClick={() => handleApprove(knowledge)}
                      className={styles["approveButton"]}
                    >
                      承認
                    </button>
                    <button
                      onClick={() => handleDiscard(knowledge)}
                      className={styles["discardButton"]}
                    >
                      破棄
                    </button>
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PendingKnowledgeModal;
